import { Injectable } from '@angular/core';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { UnifiedBankAccount } from '../../models/entities/unified-bank-account';
import { DepositBankAccount } from '../../models/entities/deposit-bank-account';
import type { TreasuryDisplayStatus } from '../stripe-treasury/treasury.service';

/** Connect account status that means KYC is complete (Treasury capability verified). */
const KYC_VERIFIED_STATUS = 'verified';

export interface BankAccountInsights {
    hasLinkedBankAccount: boolean;
    hasTreasuryAccount: boolean;
    hasAnyAccount: boolean;
}

/**
 * Derives view-layer booleans from a loaded bank-accounts collection and
 * centralizes the gate for the "Create Deposit on Transaction Ledger" toggle.
 *
 * The toggle is suppressed when EITHER:
 *   - A Plaid-linked deposit account is present (existing legacy rule), OR
 *   - The stripe-treasury.enabled FF is on AND a Treasury financial account
 *     is provisioned on the org (the SD-7291 rule — FF + data combined).
 *
 * Used by both Record Payment modals (legacy + v2) and both Import Payment
 * flows (legacy lockbox + v2 options dialog). See SD-7291 for the original
 * scatter-plot of duplicated derivation logic this consolidates.
 */
@Injectable({ providedIn: 'root' })
export class BankAccountInsightsService {
    /**
     * Special-case org excluded from Plaid auto-detection — uses manual deposit flow.
     * Inherited from pre-existing legacy logic; preserved here for parity.
     */
    private static readonly PLAID_EXCLUDED_ORG_ID = 22309;

    /**
     * Compute view-layer booleans from a loaded bank-accounts collection.
     * Works on either UnifiedBankAccount[] or DepositBankAccount[] — both
     * expose the relevant fields directly or via .unifiedBankAccount.
     */
    derive(
        accounts: ReadonlyArray<UnifiedBankAccount | DepositBankAccount>,
        organizationId: number,
    ): BankAccountInsights {
        return {
            hasLinkedBankAccount:
                accounts.some(a => this.isPlaidLinked(a))
                && organizationId !== BankAccountInsightsService.PLAID_EXCLUDED_ORG_ID,
            hasTreasuryAccount: this.hasTreasuryAccount(accounts),
            hasAnyAccount: accounts.length > 0,
        };
    }

    /**
     * Find the Treasury financial account within a loaded bank-accounts collection,
     * if one is provisioned. Generic over both account types so callers keep their
     * concrete type (e.g. a UnifiedBankAccount's .depositBankAccount stays reachable).
     */
    findTreasuryAccount<T extends UnifiedBankAccount | DepositBankAccount>(
        accounts: ReadonlyArray<T>,
    ): T | undefined {
        return accounts.find(a => a.isTreasuryAccount);
    }

    /**
     * Whether a Treasury financial account is provisioned within a loaded
     * bank-accounts collection. Single source of truth for the FA-presence check —
     * derive() and all gate sites delegate here.
     */
    hasTreasuryAccount(
        accounts: ReadonlyArray<UnifiedBankAccount | DepositBankAccount>,
    ): boolean {
        return accounts.some(a => a.isTreasuryAccount);
    }

    /**
     * Whether Stripe Treasury is enabled for the current org via JWT feature flag.
     * Sync — sources from the JWT claims, no HTTP.
     */
    hasTreasuryEnabled(): boolean {
        return !!LegFiJwtService.featureEnabled('stripe-treasury.enabled');
    }

    /**
     * Whether the org's Treasury Balance is in the post-KYC provisioning window (SD-7931):
     * Treasury is enabled (FF), KYC is verified (Connect status), but the Treasury financial
     * account has not been provisioned onto a bank account yet. During this window the
     * Accounts/Bank Accounts surfaces should show a provisioning placeholder instead of the
     * generic empty state, and swap to the real account once the readiness broadcast fires.
     * Single source of truth so the dashboard widget and Settings surface stay in agreement.
     */
    isTreasuryBalanceProvisioning(
        accounts: ReadonlyArray<UnifiedBankAccount | DepositBankAccount>,
        connectStatus: TreasuryDisplayStatus | null,
    ): boolean {
        return this.hasTreasuryEnabled()
            && connectStatus === KYC_VERIFIED_STATUS
            && !this.hasTreasuryAccount(accounts);
    }

    /**
     * SD-7931: Treasury org whose connect-status hasn't resolved yet (fetched async on init, starts
     * null). Callers keep the loader up while this is true so a fast accounts fetch can't flash the
     * generic empty state before status lands and the provisioning placeholder can render.
     */
    isTreasuryStatusPending(connectStatus: TreasuryDisplayStatus | null): boolean {
        return this.hasTreasuryEnabled() && connectStatus === null;
    }

    /**
     * Whether the "Create Deposit on Transaction Ledger" toggle should be visible.
     * Suppressed when a Plaid-linked account is present, OR when the org has
     * Treasury both enabled (FF) and provisioned (FA on a loaded bank account).
     */
    shouldShowLedgerToggle(insights: BankAccountInsights): boolean {
        return !insights.hasLinkedBankAccount
            && !(this.hasTreasuryEnabled() && insights.hasTreasuryAccount);
    }

    private isPlaidLinked(account: UnifiedBankAccount | DepositBankAccount): boolean {
        if (account instanceof UnifiedBankAccount) {
            return account.plaidTokenId != null;
        }
        return account.unifiedBankAccount != null
            && account.unifiedBankAccount.plaidTokenId != null;
    }
}
